import React, { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useComparatore, useIsMobile, useTrans, useTealium, useFindMenu } from '@next-nx/hooks'
import {
  AttributoType,
  CategoriaType,
  ListingType,
  OrdinamentiType,
  OffertaType,
} from '@next-nx/utils/types'
import { Bin, Filters, FiltersTriangle } from '@next-nx/shared-ui/icons'
import {
  Option,
  Dropdown,
  Button,
  Tag,
  SkeletonLoading,
  WContainer,
} from '@next-nx/shared-ui/atoms'
import { OffertaCard } from '@next-nx/shared-ui/molecules'
import { Filters as FiltersPanel } from '@next-nx/shared-ui/organisms'
import { api, getTimestamp } from '@next-nx/utils'
import { LISTING } from '@next-nx/utils/endpoints'
import {
  CATEGORIA_TIPO_LISTING_PRIMARIO,
  DOMAIN,
  HEADER_MAIN_HEIGHT,
  OFFERTE_LISTING_ITEMS_PER_PAGE,
} from '@next-nx/utils/settings'
import styles from './ProdottiListing.module.sass'
import { useRouter } from 'next/router'
import { getIcon } from '@next-nx/utils/icons'
import { setPagina } from '@next-nx/shared-containers'
import { useDispatch, useSelector } from 'react-redux'
import Link from 'next/link'

interface Props {
  listing: ListingType
  categoria: CategoriaType
  filtro_categoria: string
  search?: string
}

const ProdottiListing = (props: Props) => {
  const { listing, categoria, filtro_categoria, search = '' } = props

  const t = useTrans()
  const router = useRouter()
  const isMobile = useIsMobile()
  const { pagina } = useSelector((state: any) => state?.pagina)
  const dispatch = useDispatch()

  const listingMenu = useFindMenu('listing')
  const emptyListingOfferteMenu = useFindMenu('empty-listing', listingMenu)
  const emptyListingOfferte =
    emptyListingOfferteMenu?.children?.edges
      .filter((x) => x.node.offerta)
      .map((x) => x.node.offerta) || []

  const listingRef = useRef(null)
  const [stickyFiltersVisible, setStickyFiltersVisible] = useState(false)

  const handleScroll = () => {
    setStickyFiltersVisible(
      !!listingRef?.current &&
        window.scrollY > listingRef.current.offsetTop - HEADER_MAIN_HEIGHT &&
        window.scrollY <
          listingRef.current.offsetTop - HEADER_MAIN_HEIGHT + listingRef.current.clientHeight - 150 // offset
    )
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const categorieToOptions = (categorie: CategoriaType[]) => {
    return categorie.map((c) => ({
      ...c,
      label: c.titolo,
      value: c.codice,
    }))
  }

  const attributiToFilters = (attributi: AttributoType[]) => {
    return attributi.map((a) => ({
      ...a,
      valori_disponibili: a.valori_disponibili.map((v) => ({
        ...v,
        label: v.nome_completo,
        value: v.pk,
      })),
    }))
  }

  const attributiToSelectedFilters = (attributi: AttributoType[]) => {
    return attributi.reduce(
      (acc, x) => ({
        ...acc,
        [x.chiave]: x.valori_disponibili.filter((v) => v.selezionato).map((v) => v.pk),
      }),
      {}
    )
  }

  const attributiToDisabledFilters = (attributi: AttributoType[]) => {
    return attributi.reduce(
      (acc, x) => ({
        ...acc,
        [x.chiave]: x.valori_disponibili.filter((v) => v.disabilitato).map((v) => v.pk),
      }),
      {}
    )
  }

  const ordinamentiToOrdinamenti = (ordinamenti: OrdinamentiType[]) => {
    return ordinamenti.map((o) => ({
      label: o.placeholder,
      value: o.value,
    }))
  }

  const [items, setItems] = useState(listing?.elementi || [])
  const [itemsInfinite, setItemsInfinite] = useState<OffertaType[]>([])
  const [numItems, setNumItems] = useState(listing?.number)
  const [totalNumItems, setTotalNumItems] = useState(listing?.number)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const [page, setPage] = useState(listing?.page)
  const [numPages, setNumPages] = useState(listing?.paginator?.num_pages)

  const [options, setOptions] = useState(categorieToOptions(categoria.categoriePrimarie))
  const [selectedOption, setSelectedOption] = useState(filtro_categoria)

  const [filters, setFilters] = useState(attributiToFilters(listing.attributi))
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: number[] }>(
    attributiToSelectedFilters(listing.attributi)
  )
  const [disabledFilters, setDisabledFilters] = useState<{ [key: string]: number[] }>(
    attributiToDisabledFilters(listing.attributi)
  )

  const [ordinamenti, setOrdinamento] = useState(ordinamentiToOrdinamenti(listing.ordinamenti))
  const [selectedOrdinamento, setSelectedOrdinamento] = useState(
    ordinamentiToOrdinamenti(listing.ordinamenti).find((o) => o.value === listing.ordine) ||
      ordinamentiToOrdinamenti(listing.ordinamenti)[0]
  )

  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false)
  const { linkDataLayer, getListingProdottiData } = useTealium()

  const { maxCompareItems, compareItems, setCompareItems, setLimitReached, firstTime } =
    useComparatore()

  const handleTracking = (
    action_type: string = '',
    cta: string = '',
    effect?: string,
    interazione?: string,
    view_items?: OffertaType[]
  ) => {
    let dlObj = {}
    const action_timestamp = getTimestamp()

    if (action_type.charAt(action_type.length - 1) === '_') {
      action_type = action_type.substring(0, action_type.length - 1)
    }

    switch (interazione) {
      case 'filtri_quick':
        dlObj = {
          virtual: 'action',
          action_name: 'filtri_quick',
          action_tech: 'click',
          action_type: action_type.replace(/ /g, '_').toLowerCase(),
          action_timestamp,
          action_effect: `${!!effect?.length ? effect : 'visualizzazione'}_risultati_filtrati`,
        }
        break
      case 'ordinamento':
        dlObj = {
          virtual: 'action',
          action_name: 'ordinamento',
          action_tech: 'click',
          action_type: action_type.replace(/ /g, '_').toLowerCase(),
          action_timestamp,
          action_effect: 'modifica_ordinamento_prodotti',
        }
        break
      case 'carica_altri':
        dlObj = {
          virtual: 'action',
          action_name: 'carica_altri_prodotti',
          action_tech: 'click',
          action_type: action_type.replace(/ /g, '_').toLowerCase(),
          action_timestamp,
          action_effect: 'caricamento_altri_prodotti',
        }
        break
      case 'view_items':
        dlObj = {
          virtual: 'action',
          action_name: 'items_view',
          action_tech: 'view',
          action_type: 'listing',
          action_timestamp,
          action_effect: 'visualizzazione_prodotti',
          view_items: getListingProdottiData(
            view_items,
            selectedOption,
            !!search ? 'listing_ricerca' : 'listing_navigazione'
          ),
        }
        break
      case 'view_other_items':
        dlObj = {
          virtual: 'action',
          action_name: 'items_view',
          action_tech: 'view',
          action_type: 'listing',
          action_timestamp,
          action_effect: 'visualizzazione_carica_altri_prodotti',
          view_items: getListingProdottiData(
            view_items,
            selectedOption,
            !!search ? 'listing_ricerca' : 'listing_navigazione'
          ),
        }
        break
      case 'rimozione_filtro':
        dlObj = {
          virtual: 'action',
          action_name: 'filtri',
          action_tech: 'click',
          action_type: action_type.replace(/ /g, '_').toLowerCase(),
          action_timestamp,
          action_effect: 'rimozione_risultati_filtrati',
          cta: cta?.replace(/\/|\s+/g, '_').toLowerCase(),
        }
        break
      case 'aggiungi_filtro':
        dlObj = {
          virtual: 'action',
          action_name: 'filtri',
          action_tech: 'click',
          action_type: action_type.replace(/ /g, '_').toLowerCase(),
          action_timestamp,
          action_effect: 'visualizzazione_risultati_filtrati',
          cta: cta?.replace(/\/|\s+/g, '_').toLowerCase(),
        }
        break
      default:
        break
    }
    linkDataLayer(dlObj)
  }

  useEffect(() => {
    if (items || itemsInfinite) {
      setNumItems(items.length + itemsInfinite.length)
    }
  }, [items, itemsInfinite])

  useEffect(() => {
    if (items) {
      handleTracking('', '', '', 'view_items', items)
    }
  }, [items])

  const handleOptions = async (optionValue: string) => {
    const newSelectedOption =
      options?.find((x) => x.value === optionValue)?.codice || options[0].codice

    let vOpt = optionValue === 'aziende-e-partite-iva' ? 'business_piva' : optionValue

    if (newSelectedOption !== selectedOption && !!selectedOption.length) {
      let sOpt = selectedOption === 'aziende-e-partite-iva' ? 'business_piva' : selectedOption

      handleTracking(
        `rimuovi_${t('Per')}_${sOpt.replace(/\-|\/|\s+/g, '_').toLowerCase()}`,
        '',
        'rimozione',
        'filtri_quick'
      )
    }

    handleTracking(
      `aggiungi_${t('Per')}_${vOpt.replace(/\-|\/|\s+/g, '_').toLowerCase()}`,
      '',
      '',
      'filtri_quick'
    )

    if (categoria.tipo !== CATEGORIA_TIPO_LISTING_PRIMARIO) {
      setSelectedOption(newSelectedOption)

      fetchListing(1, selectedFilters, selectedOrdinamento, newSelectedOption)
    }
  }

  const handleFilters = async (
    action: string,
    attributoId: string,
    valoreId: number | number[],
    label?: string,
    filtroId?: number
  ) => {
    let newSelectedFilters = { ...selectedFilters }

    switch (action) {
      case 'remove_all':
        newSelectedFilters = filters.reduce((acc, x) => ({ ...acc, [x.chiave]: [] }), {})
        handleTracking('cancella_tutti', 'cancella_tutti', 'rimozione', 'rimozione_filtro')
        break
      case 'remove':
        newSelectedFilters = {
          ...selectedFilters,
          [attributoId]: selectedFilters[attributoId].filter((f) => f !== valoreId),
        }
        handleTracking(
          `rimuovi_${filters.filter((filter) => filter.chiave === attributoId)?.[0]?.nome}`,
          filters
            .filter((filter) => filter.chiave === attributoId)?.[0]
            ?.valori_disponibili.filter((o) => o.pk === valoreId)?.[0].nome,
          'rimozione',
          'rimozione_filtro'
        )
        break
      case 'toggle':
        if (typeof valoreId === 'object')
          newSelectedFilters = { ...selectedFilters, [attributoId]: valoreId }

        if (!!selectedFilters[attributoId].find((i) => +i === filtroId)) {
          handleTracking(
            `rimuovi_${label?.replace(/ /g, '_').toLowerCase()}`,
            filters
              .filter((filter) => filter.chiave === attributoId)?.[0]
              ?.valori_disponibili.find((item) => item.pk === filtroId)?.nome,
            '',
            'rimozione_filtro'
          )
        } else {
          let valId = typeof valoreId === 'number' ? valoreId : valoreId[valoreId.length - 1]
          handleTracking(
            `aggiungi_${
              label
                ? label?.replace(/ /g, '_').toLowerCase()
                : filters
                    .filter((filter) => filter.chiave === attributoId)?.[0]
                    ?.valori_disponibili.find((item) => item.pk === valId)?.nome
            }`,
            filters
              .filter((filter) => filter.chiave === attributoId)?.[0]
              ?.valori_disponibili.find((item) => item.pk === valId)?.nome,
            '',
            'aggiungi_filtro'
          )
        }
        break
    }

    setSelectedFilters(newSelectedFilters)

    fetchListing(1, newSelectedFilters)
  }

  const handleOrdinamento = (ordinamentoValue: string) => {
    const newSelectedOrdinamento =
      ordinamenti.find((x) => x.value === ordinamentoValue) || ordinamenti[0]

    handleTracking(
      `ordina_${newSelectedOrdinamento.label.replace(/\(|\)|\/|\s+/g, '_').toLowerCase()}`,
      '',
      '',
      'ordinamento'
    )

    setSelectedOrdinamento(newSelectedOrdinamento)

    fetchListing(1, selectedFilters, newSelectedOrdinamento)
  }

  const setListing = async (listing: ListingType) => {
    const page = listing.page

    if (page > 1) {
      setItemsInfinite([...itemsInfinite, ...listing.elementi])
      handleTracking('', '', '', 'view_other_items', [...listing.elementi])
    } else {
      setItems([...listing.elementi])
      setItemsInfinite([])
    }

    setPage(page)
    setNumPages(listing.paginator.num_pages)
    setTotalNumItems(listing.number)
    setFilters(attributiToFilters(listing.attributi))
    setSelectedFilters(attributiToSelectedFilters(listing.attributi))
    setDisabledFilters(attributiToDisabledFilters(listing.attributi))
    setOrdinamento(ordinamentiToOrdinamenti(listing.ordinamenti))
    setSelectedOrdinamento(
      ordinamentiToOrdinamenti(listing.ordinamenti).find((o) => o.value === listing.ordine) ||
        ordinamentiToOrdinamenti(listing.ordinamenti)[0]
    )
    setLoading(false)
  }

  const fetchListing = async (
    newPage = 1,
    newFilters = selectedFilters,
    newSelectedOrdinamento = selectedOrdinamento,
    newSelectedOption = selectedOption
  ) => {
    newPage === 1 && setLoading(true)

    const selectedOptionCategoria = options.find((o) => o.value === newSelectedOption) || options[0]

    const filtriId = Object.keys(newFilters)
      .map((attributoId) => newFilters[attributoId])
      .filter((attr) => attr.length > 0) // solo quelli con almento 1 valore selezionato
      .reduce((a, b) => a.concat(b), []) // unisco in array a 1 livello
      .join(',')

    const params = {
      categoria_id: categoria.pk,
      categoria: selectedOptionCategoria.codice,
      p: newPage,
      o: newSelectedOrdinamento.value,
      per_page: OFFERTE_LISTING_ITEMS_PER_PAGE,
      filtri_id: filtriId,
      search: search ?? null,
    }

    const response = await api.get(LISTING, { params })
    const listing = response.data?.listing

    if (selectedOrdinamento?.label !== newSelectedOrdinamento?.label) {
      router.query['o'] = newSelectedOrdinamento.value
    }
    if (newPage !== page && newPage !== 1) {
      router.query['p'] = newPage.toString()
    } else {
      delete router.query.p
    }
    filtriId ? (router.query.filtri_id = filtriId) : delete router.query.filtri_id
    selectedOptionCategoria.codice !== categoria.codice
      ? (router.query.categoria = selectedOptionCategoria.codice)
      : delete router.query.categoria
    selectedOptionCategoria.codice !== filtro_categoria && delete router.query.prezzi

    await dispatch(setPagina({ ...pagina, listing: listing }))
    await router.push(
      {
        pathname: router.pathname,
        query: router.query,
      },
      undefined,
      { shallow: true }
    )

    setListing(listing)
  }

  const loadMore = async () => {
    if (page < numPages) {
      setLoadingMore(true)

      handleTracking(
        !!search?.length ? 'listing_page_ricerca' : 'listing_page_navigazione',
        '',
        '',
        'carica_altri'
      )
      await fetchListing(page + 1)

      setLoadingMore(false)
    }
  }

  return (
    <>
      {options && options?.length > 0 && (
        <div className={styles.options}>
          {options.map((x) => (
            <Option
              icon={getIcon(x.icona)}
              label={`${t('Per')} ${x.nome}`}
              active={selectedOption === x.value}
              onClick={() => handleOptions(x.value)}
              url={categoria.tipo === CATEGORIA_TIPO_LISTING_PRIMARIO ? x.url : undefined}
              key={x.pk}
            />
          ))}
        </div>
      )}
      <FiltersPanel
        open={filtersPanelOpen}
        setOpen={setFiltersPanelOpen}
        ordinamenti={ordinamenti}
        selectedOrdinamento={selectedOrdinamento}
        filters={filters}
        selectedFilters={selectedFilters}
        disabledFilters={disabledFilters}
        handleFilters={handleFilters}
        handleOrdinamento={handleOrdinamento}
        totalResults={totalNumItems}
        handleTracking={handleTracking}
      />
      <div
        className={`${styles.stickyFilters} ${
          stickyFiltersVisible ? styles['stickyFilters--visible'] : ''
        }`}
      >
        <WContainer variant="lg">
          <div className={styles.stickyFilterSlider}>
            <Swiper slidesPerView={'auto'} spaceBetween={24}>
              {/* {!!options.length && (
                <SwiperSlide>
                  <Dropdown
                    label={'Seleziona'}
                    options={options.map((x) => ({
                      label: `${t('Per')} ${x.nome}`,
                      value: x.pk,
                    }))}
                    isMulti
                    value={selectedOption}
                    setValue={(v) => handleOptions(v.value)}
                  />
                </SwiperSlide>
              )} */}
              {filters.length > 0 &&
                filters
                  .filter((x) => x.valori_disponibili?.length > 0)
                  .map((x) => (
                    <SwiperSlide key={x.pk}>
                      <Dropdown
                        label={x.nome}
                        options={x.valori_disponibili}
                        isMulti
                        value={selectedFilters[x.chiave]}
                        setValue={(v, f) => handleFilters('toggle', x.chiave, v, x.nome, f)}
                        disabledValue={disabledFilters[x.chiave]}
                        filled
                        optionVariant="checkbox"
                      />
                    </SwiperSlide>
                  ))}
            </Swiper>
          </div>
          <div className={styles.stickyFiltersMobile}>
            <span className={styles.stickyFiltersMobileListNum}>
              {totalNumItems === 1 ? (
                <>{t('1 prodotto trovato')}</>
              ) : (
                <>{t('{0} prodotti trovati', [totalNumItems.toString()])}</>
              )}
            </span>
            <div className={styles.stickyFiltersMobileListActionWrap}>
              <Button
                label={t('Filtra e ordina')}
                icon={<Filters />}
                variant="ghost"
                color="blue"
                className={styles.stickyFiltersMobileListActionMobile}
                onClick={() => setFiltersPanelOpen(true)}
              />
              {Object.values(selectedFilters).reduce((count, x) => count + x.length, 0) > 0 && (
                <span className={styles.stickyFiltersMobileListActionBadge}>
                  {Object.values(selectedFilters).reduce((count, x) => count + x.length, 0)}
                </span>
              )}
            </div>
          </div>
        </WContainer>
      </div>
      {filters.length > 0 && (
        <div className={styles.filters}>
          {filters
            .filter((x) => x.valori_disponibili?.length > 0)
            .map((x) => (
              <Dropdown
                label={x.nome}
                options={x.valori_disponibili}
                isMulti
                value={selectedFilters[x.chiave]}
                setValue={(v, f) => handleFilters('toggle', x.chiave, v, x.nome, f)}
                disabledValue={disabledFilters[x.chiave]}
                filled
                optionVariant="checkbox"
                key={x.pk}
              />
            ))}
        </div>
      )}
      {Object.values(selectedFilters).filter((x) => x.length > 0).length > 0 && (
        <div className={styles.selectedFilters}>
          {Object.keys(selectedFilters)
            .filter((k) => selectedFilters[k].length > 0)
            .map((k) =>
              selectedFilters[k].map((x) => (
                <Tag
                  label={
                    filters
                      .filter((filter) => filter.chiave === k)?.[0]
                      ?.valori_disponibili.filter((o) => o.pk === x)?.[0].nome
                  }
                  color="blue"
                  onClose={() => handleFilters('remove', k, x)}
                  key={`${k}-${x}`}
                />
              ))
            )}
          <Button
            label={t('Cancella tutti')}
            icon={<Bin />}
            variant="ghost"
            onClick={() => handleFilters('remove_all', '', 0)}
          />
        </div>
      )}
      <div className={styles.listHeader}>
        <span className={styles.listNum}>
          {totalNumItems === 1 ? (
            <>{t('1 prodotto trovato')}</>
          ) : (
            <>{t('{0} prodotti trovati', [totalNumItems.toString()])}</>
          )}
        </span>
        <div className={styles.listActions}>
          <Dropdown
            label={t('Ordina per: {0}', [selectedOrdinamento.label])}
            options={ordinamenti}
            value={selectedOrdinamento.value}
            setValue={(v) => handleOrdinamento(v)}
            icon={<FiltersTriangle />}
            highlightOnCompiled={false}
            dropdownVariant="arrow"
            className={styles.listOrder}
          />
          <div className={styles.listActionMobileWrap}>
            <Button
              label={t('Filtra e ordina')}
              icon={<Filters />}
              variant="ghost"
              color="blue"
              className={styles.listActionMobile}
              onClick={() => setFiltersPanelOpen(true)}
            />
            {Object.values(selectedFilters).reduce((count, x) => count + x.length, 0) > 0 && (
              <span className={styles.listActionMobileBadge}>
                {Object.values(selectedFilters).reduce((count, x) => count + x.length, 0)}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className={styles.listing} ref={listingRef}>
        <div className={`${styles.items} ${items.length == 0 ? styles['items--empty'] : ''}`}>
          {loading ? (
            <>
              {Array(isMobile ? 4 : 16)
                .fill('')
                .map((_, i) => (
                  <SkeletonLoading key={i} />
                ))}
            </>
          ) : (
            items.map((x) => (
              <OffertaCard
                key={x.pk}
                offerta={x}
                codiceCategoriaPrimaria={selectedOption}
                comparable
                compareActive={compareItems.filter((ci) => ci.pk === x.pk).length > 0}
                cta="icona_plp"
                sezione="listing"
                onCompare={
                  compareItems.filter((ci) => ci.pk === x.pk).length > 0
                    ? () => setCompareItems(compareItems.filter((ci) => ci.pk !== x.pk))
                    : compareItems.length < maxCompareItems
                    ? () => setCompareItems(compareItems.concat([x]))
                    : () => setLimitReached(true)
                }
                showTooltip={firstTime}
              />
            ))
          )}
          {itemsInfinite &&
            itemsInfinite.map((x) => (
              <OffertaCard
                key={x.pk}
                offerta={x}
                codiceCategoriaPrimaria={selectedOption}
                comparable
                compareActive={compareItems.filter((ci) => ci.pk === x.pk).length > 0}
                cta="icona_plp"
                sezione="listing"
                onCompare={
                  compareItems.filter((ci) => ci.pk === x.pk).length > 0
                    ? () => setCompareItems(compareItems.filter((ci) => ci.pk !== x.pk))
                    : compareItems.length < maxCompareItems
                    ? () => setCompareItems(compareItems.concat([x]))
                    : () => setLimitReached(true)
                }
                showTooltip={firstTime}
              />
            ))}
        </div>
        {page < numPages && (
          <div className={styles.loadMore}>
            <div className={styles.loadMoreLabel}>
              {t('Hai visualizzato {0} prodotti di {1}', [
                numItems.toString(),
                totalNumItems.toString(),
              ])}
            </div>
            <div className={styles.loadMoreCtaBox}>
              <Button
                label={'Carica altri'}
                color="blue"
                onClick={() => loadMore()}
                loading={loadingMore}
                href={{
                  pathname: router.pathname,
                  query: { ...router.query, p: page + 1 },
                }}
                shallow={true}
              />
            </div>
          </div>
        )}
        {items.length == 0 && (
          <div className={styles.emptyListingContainer}>
            <div className={styles.emptyListingDescription}>
              <p>
                {t('Non abbiamo trovato veicoli che rispecchiano la tua ricerca, ci dispiace!')}
              </p>
              {emptyListingOfferte.length > 0 && <p>{t('Ecco la nostra selezione per te')}</p>}
            </div>
            <div className={styles.listing}>
              <div className={`${styles.items}`}>
                {emptyListingOfferte.map((x) => (
                  <OffertaCard
                    key={x.pk}
                    offerta={x}
                    codiceCategoriaPrimaria={selectedOption}
                    comparable
                    compareActive={compareItems.filter((ci) => ci.pk === x.pk).length > 0}
                    cta="icona_plp"
                    onCompare={
                      compareItems.filter((ci) => ci.pk === x.pk).length > 0
                        ? () => setCompareItems(compareItems.filter((ci) => ci.pk !== x.pk))
                        : compareItems.length < maxCompareItems
                        ? () => setCompareItems(compareItems.concat([x]))
                        : () => setLimitReached(true)
                    }
                    showTooltip={firstTime}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <noscript>
        {listing?.paginator.page_range.map((page) => (
          <a href={DOMAIN + router.asPath.split('?')?.[0] + '?p=' + page}>Pagina {page}</a>
        ))}
      </noscript>
    </>
  )
}

export default ProdottiListing
